/**
 * Tipos para CatastroGeocoderV2
 *
 * Define interfaces para los datos servidos por el Worker R2
 * y las opciones de geocodificación.
 *
 * @module types/catastroR2
 * @version 2.0.0 — C3.09: CatastroR2MetaIndice + type guard + helper
 */

import type { ProvinciaAndalucia } from './catastro';
import { TipologiaPTEL, TIPOLOGIAS_VALUES } from './tipologia';

// =============================================================================
// DATOS DEL WORKER R2
// =============================================================================

/** Parcela catastral servida por Worker R2 */
export interface CatastroR2Parcel {
  refcat: string;
  /**
   * Referencia catastral canónica de 14 chars (`line[30:44]` del registro tipo 11
   * del `.CAT`), emitida por `scripts/cat2ptel_fix.py` desde RUSTICA-FIX S1.
   * Corrige el offset legacy `[23:37]` que colapsaba la rústica por colisión de
   * clave (Colomera: 5942→75) y le robaba las coordenadas al deduplicar.
   * Desde LUR1 (jun-2026) el servicio consume el unificado cat-rich-1 y la clave
   * está SIEMPRE presente en dato vivo; sigue opcional por compatibilidad con
   * fixtures legacy — los consumidores leen `refcat_canonico ?? refcat`.
   */
  refcat_canonico?: string;
  x: number;
  y: number;
  via: string;
  nombre_via: string;
  numero: string;
  uso: string;
  uso_cod: string;
  destino: string;
  destino_cod: string;
  municipio: string;
  cod_ine: string;
  sup_total?: number;
  sup_construida?: number;
  antiguedad?: number;
  plantas?: number;
  /**
   * Clase de bien del split legacy `_U`/`_R`. El unificado cat-rich-1 (LUR1)
   * NO expone este campo — verificado empíricamente 11-jun (28 claves, ninguna
   * de clase); las rústicas se distinguen por `cultivos[]`. Se conserva opcional
   * para fixtures legacy. Ningún consumidor productivo filtra por él (censo LUR1).
   */
  tipo_bien?: 'U' | 'R';
  // ——— Claves cat-rich-1 (HX-CAPTURA-MAXIMAL · presentes desde LUR1) ———
  /** SRS servido (siempre `EPSG:25830` post-reproyección huso-29 aditiva). */
  srs?: string;
  /** SRS de origen del `.CAT` (p. ej. `EPSG:25829` en municipios huso-29). */
  srs_origen?: string;
  /** Coordenadas originales pre-reproyección (aditivas, no destructivas). */
  x_origen?: number;
  y_origen?: number;
  sup_porches?: number;
  sup_otras_plantas?: number;
  /** Año de reforma (registro tipo 14/15 del `.CAT`). */
  anyo_reforma?: number;
  /** Tipo de reforma (`.CAT` t14, FAQ DGC §14): `R` rehab. integral · `O` reforma
   *  total · `E` reforma media · `I` reforma mínima. */
  tipo_reforma?: string;
  /** Indicador de exactitud del año de constr. (`.CAT` t13, FAQ DGC §12):
   *  `E` exacto · `+` aprox. por encima · `-` aprox. por debajo · `C` aproximada. */
  antiguedad_exactitud?: string;
  /** Subparcelas de cultivo (solo rústicas) — sustituye al split `_R`. */
  cultivos?: Array<Record<string, unknown>>;
  /** Provenance del registro (pipeline cat-rich-1). */
  provenance?: Record<string, unknown>;
  /** Aforo calculado por pipeline Python (C3.12). GPKG o fórmula derivada. */
  aforo_estimado?: number | null;
  /** Fuente del aforo: GeoPackage PTEL (preciso) o fórmula derivada (±25-33 %) */
  aforo_metodo?: 'gpkg' | 'formula';
  /** true en usos que CTE-DB-SI infravalora ~25-33 % (24 usos identificados) */
  aforo_conservador?: boolean | null;
}

// =============================================================================
// C3.09 — METAÍNDICE U/R
// =============================================================================

/**
 * Conteo de parcelas para un municipio en el metaíndice de Catastro.
 * Generado por _build_indice() en enrich_catastro_aforo.py (C3.09).
 */
export interface CatastroR2MetaIndice {
  /** Parcelas urbanas  ({ine5}_U.json.gz) */
  U: number;
  /** Parcelas rústicas ({ine5}_R.json.gz) */
  R: number;
  /** Total = U + R  (invariante verificado por isCatastroR2MetaIndice) */
  total: number;
}

/**
 * Type guard: comprueba que un objeto desconocido es CatastroR2MetaIndice
 * y que el invariante total === U + R se cumple.
 */
export function isCatastroR2MetaIndice(obj: unknown): obj is CatastroR2MetaIndice {
  if (typeof obj !== 'object' || obj === null) return false;
  const o = obj as Record<string, unknown>;
  return (
    typeof o['U']     === 'number' &&
    typeof o['R']     === 'number' &&
    typeof o['total'] === 'number' &&
    o['total'] === (o['U'] as number) + (o['R'] as number)
  );
}

/**
 * Recupera la entrada del índice para un municipio dado.
 * Devuelve null si el meta no tiene índice o el municipio no está.
 */
export function getIndiceEntry(
  meta: CatastroR2Meta,
  ine5: string,
): CatastroR2MetaIndice | null {
  return meta.indice?.[ine5] ?? null;
}

/** Resultado discriminado del fetch /catastro/meta del Worker */
export type CatastroMetaResult =
  | { ok: true;  meta: CatastroR2Meta }
  | { ok: false; error: string };

// =============================================================================
// METADATOS GLOBALES
// =============================================================================

/** Metadatos globales Catastro R2 (meta.json.gz en R2) */
export interface CatastroR2Meta {
  registros?: number;
  municipios?: number;
  /** Versión del pipeline (ej: "4.0.0") */
  version: string;
  /** Fecha de generación ISO-8601 (ej: "2026-03-11") */
  fecha: string;
  fuente?: string;
  srs?: string;
  /** Campos incluidos en cada registro NDJSON. Presente desde v4.0.0 (C3.09). */
  campos?: string[];
  /**
   * Índice de municipios con datos disponibles.
   * Clave: código INE5 (5 dígitos, ej: "18001").
   * Ausente en meta.json anteriores a v4.0.0 → la UI debe manejar undefined.
   */
  indice?: Record<string, CatastroR2MetaIndice>;
}

// =============================================================================
// OPCIONES DE GEOCODIFICACIÓN
// =============================================================================

export interface CatastroR2SearchOptions {
  codINE: string;
  name?: string;
  refcat?: string;
  via?: string;
  numero?: string;
  polygon?: string;
  parcel?: string;
  usos?: string[];
  minConfidence?: number;
  maxResults?: number;
  /**
   * Para fallback OVC online (`geocodeWithOVCFallback`): nombre del municipio.
   * R2 no usa este campo en su pipeline; sólo se lee al escalar a OVC porque
   * `CatastroService.geocode` lo exige. Camino A — F1-R2-FIRST chat 1/2 V2.
   */
  municipality?: string;
  /**
   * Para fallback OVC online (`geocodeWithOVCFallback`): nombre de la provincia.
   * Mismas semánticas que `municipality`. Camino A — F1-R2-FIRST chat 1/2 V2.
   */
  province?: string;
}

export interface CatastroR2Result {
  x: number;
  y: number;
  confidence: number;
  precision: number;
  source: 'catastro_r2';
  parcel: CatastroR2Parcel;
  fuzzyScore?: number;
}

// =============================================================================
// MAPEO TIPOLOGÍA → USOS CATASTRO (adaptador formal)
// =============================================================================

/**
 * Tabla de usos de la DGC, derivada del dato y no de memoria.
 *
 * Pares `(uso_cod, uso)` distintos del volcado R2 de Colomera (INE 18051,
 * n=6 876 parcelas), medidos en `FUS1` 02-ago-2026. Concuerda en los 8 códigos
 * compartidos con `USO_DESCRIPTIONS` (`./catastro`), que es la única tabla
 * declarada dentro del repo y la que consume `lambda/parsers/catParser.ts`.
 *
 *   E Educativo · Y Sanitario · P Público · R Religioso · K Deportivo
 *   T Espectáculos · G **Turismo** · I Industrial · C Comercial
 *   V Vivienda · A Almacén · M **Suelo sin edificar**
 *   O, J, Z — sin etiqueta en el volcado; no se afirma nada sobre ellos
 *
 * Medición y censo completo: `.ptel/p0_fus1_20260802/AUDIT_FUS1.md` §1.
 */

/**
 * Adaptador formal `TipologiaPTEL` (macro-19) → códigos Catastro 1-letra.
 *
 * ⚠️ **Reescrito íntegro en `FUS1` (02-ago-2026), `DOP-FUS1-ADAPTADOR`=(c).** El mapa
 * anterior venía de `TUN1 §6.3`, que declaraba el origen de cada fila como «actual»,
 * «rename» o «nuevo» — **ninguna citaba la tabla de usos de la DGC**. Migrado por
 * analogía desde el `TIPOLOGIA_TO_CATASTRO_USOS` legacy, nunca contrastado con la
 * fuente. Resultado medido: **14 de 19 entradas arrastraban al menos un código
 * incompatible**, y 9 usaban `G` como si significara «gubernamental» cuando `G` es
 * **turismo y hostelería**. Consecuencia productiva medida sobre Colomera: la Policía
 * Local se ataba a una vivienda a 16 m (`AUDIT_FUS1` §4).
 *
 * **Procedencia declarada por entrada, en tres niveles.** No son la misma clase de
 * afirmación y mezclarlas sin etiqueta es cómo se llegó al mapa anterior:
 *
 * - `MEDIDO`    — confirmado sobre corpus real (Colomera n=8, `CCB1` §7.2 + `FUS1` §4).
 * - `TABLA`     — identidad directa con la etiqueta DGC; falsable sin muestra.
 * - `EXCLUSION` — sin corpus. La letra más estrecha que queda tras descartar las
 *                 incompatibles. **Revisable en cuanto haya muestra**; es la clase
 *                 de afirmación que la próxima medición puede corregir.
 *
 * ⚠️ **Un array vacío NO significa abstención.** `CatastroGeocoderV2.search()` filtra
 * con `if (usos?.length)`: devolver `[]` **desactiva** el filtro. Solo `GENERICO` lo
 * hace, y a propósito. Ninguna entrada puede quedarse sin código «por prudencia» —
 * sería lo contrario de lo prudente.
 *
 * @param t - Categoría macro PTEL
 * @returns Array de códigos Catastro 1-letra, o vacío solo para `GENERICO`.
 *
 * @example
 * tipologiaACodigoCatastro(TipologiaPTEL.POLICIAL);  // ['P']
 * tipologiaACodigoCatastro(TipologiaPTEL.GENERICO);  // [] — sin filtro, intencional
 */
export function tipologiaACodigoCatastro(t: TipologiaPTEL): string[] {
  switch (t) {
    // ── MEDIDO · corpus Colomera n=8 ────────────────────────────────────────
    /** Consultorio de Colomera ata al edificio Público de 2 141 m², no a `Y`. */
    case TipologiaPTEL.SANITARIO:    return ['Y', 'P'];
    /** 3/3 centros educativos confirmados a 15-27 m. */
    case TipologiaPTEL.EDUCATIVO:    return ['E'];
    /** Policía Local y Guardia Civil, ambos `P`. Era `['G','Y']` = turismo + sanidad. */
    case TipologiaPTEL.POLICIAL:     return ['P'];
    /** Ayuntamiento `P` a 15 m. `O` (oficinas) se conserva; era `['O','K']`. */
    case TipologiaPTEL.MUNICIPAL:    return ['P', 'O'];

    // ── TABLA · identidad directa con la etiqueta DGC ────────────────────────
    /** `R` = Religioso. Era `['M']` = suelo sin edificar. */
    case TipologiaPTEL.RELIGIOSO:    return ['R'];
    /** `K` = Deportivo. Era `['T']` = espectáculos. */
    case TipologiaPTEL.DEPORTIVO:    return ['K'];
    /** `I` = Industrial, `J` industrial agrario (sin etiqueta en el volcado). */
    case TipologiaPTEL.INDUSTRIAL:   return ['I', 'J'];
    /**
     * `E` cultural/educativo + `T` espectáculos. **`R` incluido a propósito**:
     * aguas arriba `mapTypeToTipologia` funde CULTURAL y RELIGIOSO en `PATRIMONIO`,
     * y sin `R` una ermita clasificada como patrimonio perdería su propia parcela.
     */
    case TipologiaPTEL.CULTURAL:     return ['E', 'T', 'R'];

    // ── EXCLUSION · sin corpus · la letra más estrecha defendible ────────────
    /** Edificio singular; `I` por los parques en nave. Era `['G']` = turismo. */
    case TipologiaPTEL.BOMBEROS:     return ['P', 'I'];
    /** CECOPAL y protección civil, edificio público; `Y` beneficencia. */
    case TipologiaPTEL.EMERGENCIAS:  return ['P', 'Y'];
    /** Sin cambio: era la única entrada con `P` y ya era defendible. */
    case TipologiaPTEL.SOCIAL:       return ['Y', 'P'];
    /** Gasolineras y depósitos. `G` retirado. */
    case TipologiaPTEL.COMBUSTIBLE:  return ['I'];
    /** Depósitos, EDAR, ETAP. `G` retirado. */
    case TipologiaPTEL.HIDRAULICO:   return ['I'];
    /** Subestaciones, plantas. `G` retirado. */
    case TipologiaPTEL.ENERGIA:      return ['I'];
    /** Centrales y antenas. Era `['K','G']` = deportivo + turismo, ambos falsos. */
    case TipologiaPTEL.TELECOM:      return ['I', 'P'];
    /** La DGC no tiene código de transporte; una estación es edificio singular. */
    case TipologiaPTEL.VIAL:         return ['P'];
    /** Instalaciones portuarias. Era `['G','T']`, ninguno válido. */
    case TipologiaPTEL.PUERTO:       return ['P', 'I'];
    /** Puntos limpios y vertederos. Sin cambio (el JSDoc previo prometía `[]`). */
    case TipologiaPTEL.RSU:          return ['I', 'J'];

    // ── Sin discriminador · desactiva el filtro, a propósito ─────────────────
    case TipologiaPTEL.GENERICO:     return [];
    default: {
      // Exhaustividad TS: si se añade una nueva cat al enum, este branch
      // deja de compilar (assertNever pattern).
      const _exhaustive: never = t;
      return _exhaustive;
    }
  }
}

/**
 * Traduce el vocabulario DERA (`InfraTipologia`) al vocabulario PTEL (`TipologiaPTEL`).
 *
 * ⚠️ **Existe porque son dos enumerados distintos y nadie los reconciliaba.**
 * `mapTypeToTipologia` (`orchestrator/typeMapping.ts`) devuelve `InfraTipologia`;
 * `tipologiaACodigoCatastro` consume `TipologiaPTEL`. Los llamantes comprobaban la
 * pertenencia con `TIPOLOGIAS_VALUES.includes(...)` y **cada discrepancia se
 * convertía en `usos: undefined`, sin aviso** — es decir, en el filtro por uso
 * apagado. Medido en `FUS1`: **6 de los 13 valores de `InfraTipologia`** caían así,
 * entre ellos `SEGURIDAD`, que es el caso peor del corpus (`AUDIT_FUS1` §3).
 *
 * Los cinco renombrados que `TUN1` introdujo sin propagar al vocabulario DERA:
 * `SEGURIDAD→POLICIAL` · `EMERGENCIA→EMERGENCIAS` · `PATRIMONIO→CULTURAL` ·
 * `TRANSPORTE→VIAL` · `RESIDUOS→RSU`. El desajuste singular/plural de `EMERGENCIA`
 * ya estaba escrito como «TD-EMERGENCIA legacy» en
 * `OBS-FT-BOMBEROS-SUBTYPE-PROPAGATION` §4.F, sin resolver.
 *
 * Acepta `string` a propósito: evita que `types/` dependa de `lib/localData/schemas`
 * (que arrastra Dexie) y admite los valores sueltos de `classification.type`.
 *
 * @param t - Valor de `InfraTipologia`, o cualquier cadena
 * @returns La `TipologiaPTEL` equivalente, o `undefined` si no la hay (`OTRO`,
 *          cadena vacía, valor desconocido). **`undefined` significa «sin filtro»:
 *          el llamante decide, y debe saber que no filtrar es la opción arriesgada.**
 */
export function infraTipologiaAPTEL(t: string | undefined | null): TipologiaPTEL | undefined {
  if (!t) return undefined;
  const directa = (TIPOLOGIAS_VALUES as readonly string[]).includes(t);
  if (directa) return t as TipologiaPTEL;
  switch (t) {
    case 'SEGURIDAD':  return TipologiaPTEL.POLICIAL;
    case 'EMERGENCIA': return TipologiaPTEL.EMERGENCIAS;
    case 'PATRIMONIO': return TipologiaPTEL.CULTURAL;
    case 'TRANSPORTE': return TipologiaPTEL.VIAL;
    case 'RESIDUOS':   return TipologiaPTEL.RSU;
    default:           return undefined;  // 'OTRO' y desconocidos
  }
}

// =============================================================================
// CONSTANTES VALIDACIÓN UTM30 ANDALUCÍA
// =============================================================================

/** Rango válido X UTM30 Andalucía (C3.01: Campo de Gibraltar ~764 000 m) */
export const X_RANGE_ANDALUCIA = { min: 50000, max: 800000 };
/** Rango válido Y UTM30 Andalucía */
export const Y_RANGE_ANDALUCIA = { min: 3950000, max: 4300000 };

export type { ProvinciaAndalucia };
